import React, { useState } from 'react'
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Popconfirm, Tag } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ColumnHeightOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { unitService } from '../services/unit.service'
import type { Unit } from '../services/unit.service'

export const UnitsScreen: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingUnit, setEditingUnit] = useState<Unit | null>(null)
    const [form] = Form.useForm()
    const queryClient = useQueryClient()

    const { data: units = [], isLoading } = useQuery({
        queryKey: ['units'],
        queryFn: unitService.getUnits,
    })

    const createMutation = useMutation({
        mutationFn: unitService.createUnit,
        onSuccess: () => {
            message.success('Unit created successfully')
            setIsModalOpen(false)
            queryClient.invalidateQueries({ queryKey: ['units'] })
        },
    })

    const updateMutation = useMutation({
        mutationFn: (data: Partial<Unit>) => unitService.updateUnit(editingUnit!.id, data),
        onSuccess: () => {
            message.success('Unit updated successfully')
            setIsModalOpen(false)
            setEditingUnit(null)
            queryClient.invalidateQueries({ queryKey: ['units'] })
        },
    })

    const deleteMutation = useMutation({
        mutationFn: unitService.deleteUnit,
        onSuccess: () => {
            message.success('Unit deleted successfully')
            queryClient.invalidateQueries({ queryKey: ['units'] })
        },
    })

    const handleAdd = () => {
        setEditingUnit(null)
        form.resetFields()
        setIsModalOpen(true)
    }

    const handleEdit = (unit: Unit) => {
        setEditingUnit(unit)
        form.setFieldsValue(unit)
        setIsModalOpen(true)
    }

    const handleSubmit = (values: any) => {
        if (editingUnit) {
            updateMutation.mutate(values)
        } else {
            createMutation.mutate(values)
        }
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Symbol',
            dataIndex: 'symbol',
            key: 'symbol',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => (
                <Tag color="blue">{type.toUpperCase()}</Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active: boolean) => (
                <Tag color={active ? 'green' : 'red'}>
                    {active ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Unit) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        type="text"
                    />
                    <Popconfirm
                        title="Delete Unit"
                        description="Are you sure to delete this unit?"
                        onConfirm={() => deleteMutation.mutate(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            type="text"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    return (
        <Card
            title={
                <Space>
                    <ColumnHeightOutlined />
                    Unit Master
                </Space>
            }
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Add Unit
                </Button>
            }
        >
            <Table
                dataSource={units}
                columns={columns}
                rowKey="id"
                loading={isLoading}
            />

            <Modal
                title={editingUnit ? "Edit Unit" : "Add Unit"}
                open={isModalOpen}
                onOk={() => form.submit()}
                onCancel={() => setIsModalOpen(false)}
                confirmLoading={createMutation.isPending || updateMutation.isPending}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ is_active: true, type: 'other' }}
                >
                    <Form.Item
                        name="name"
                        label="Unit Name"
                        rules={[{ required: true, message: 'Please enter unit name' }]}
                    >
                        <Input placeholder="e.g. Kilogram" />
                    </Form.Item>
                    <Form.Item
                        name="symbol"
                        label="Symbol"
                        rules={[{ required: true, message: 'Please enter symbol' }]}
                    >
                        <Input placeholder="e.g. kg" />
                    </Form.Item>
                    <Form.Item
                        name="type"
                        label="Type"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Select.Option value="weight">Weight</Select.Option>
                            <Select.Option value="volume">Volume</Select.Option>
                            <Select.Option value="count">Count</Select.Option>
                            <Select.Option value="area">Area</Select.Option>
                            <Select.Option value="length">Length</Select.Option>
                            <Select.Option value="other">Other</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea placeholder="Optional description" />
                    </Form.Item>
                    <Form.Item name="is_active" label="Status" valuePropName="checked">
                        <Select>
                            <Select.Option value={true}>Active</Select.Option>
                            <Select.Option value={false}>Inactive</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    )
}
